import { type ReactNode, useContext, useMemo } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import AccommodationRoomDetailsList from 'components/AccommodationKit/AccommodationRoomDetailsList'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { isHotelPackage } from 'lib/offer/offerTypes'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Pill from 'LuxKit/Pill'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import type { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import LePackageUpgradeCardRoomDetails from './LePackageUpgradeCardRoomDetails'
import usePackageUpgradeInclusions from './usePackageUpgradeInclusions'
import { highlightIcons, highlightMessages } from './stayUpgradeModalHighlights'
import {
  getStayUpgradeHighlights,
  getStayUpgradeInclusions,
  getStayUpgradeType,
} from './stayUpgradeModalUtils'

const EMPTY_INCLUSIONS: Array<App.PackageInclusion> = []

const messages = defineMessages({
  allInclusionsNewBadge: {
    id: '7JqqeQNYLt5yWdxkVy8u',
    defaultMessage: '{count} new total',
    description: 'Inclusion badge when every inclusion in the refreshed mobile room upgrade modal is new',
  },
  someInclusionsNewBadge: {
    id: 'ERpKlsVToNt-S5J7YOvu',
    defaultMessage: '{count} new',
    description: 'Inclusion badge when some inclusions in the refreshed mobile room upgrade modal are new',
  },
  noNewInclusionsBadge: {
    id: 'xPMVS1WOsGQGionqu6Td',
    defaultMessage: '{count} total',
    description: 'Inclusion badge when no inclusions in the refreshed mobile room upgrade modal are new',
  },
  inclusionsHeading: {
    id: '42MuS2hLkcqw5HKzoGfo',
    defaultMessage: 'Your upgrade includes',
    description: 'Inclusions heading shared by every upgrade type in the refreshed mobile room upgrade modal',
  },
  packageOnlyTitle: {
    id: 'xqFVXVDwyxNmvRQLHrch',
    defaultMessage: 'Get more from your stay',
    description: 'Title for a package-only upgrade in the refreshed mobile room upgrade modal',
  },
  roomUpgradeTitle: {
    id: 'fLeRs6Bs8vno765AyvPw',
    defaultMessage: 'Upgrade your stay',
    description: 'Title for a room upgrade in the refreshed mobile room upgrade modal',
  },
  upgradeButton: {
    id: '5tLaz2DuFnZe0phmbJ7f',
    defaultMessage: 'Upgrade for only {formattedUpgradePrice}',
    description: 'Primary CTA with price difference in the refreshed mobile room upgrade modal',
  },
  noThanksButton: {
    id: 'ILbJM4W8qjEJuMXasEle',
    defaultMessage: 'No thanks',
    description: 'Decline action in the refreshed mobile room upgrade modal',
  },
})

const imageProps: ImageParams = {
  mobileAspectRatio: '3:2',
  quality: 'good',
}

const HighlightIcon = styled.div`
  align-items: center;
  background-color: ${props => props.theme.palette.neutral.default.six};
  border-radius: 50%;
  display: flex;
  flex: 0 0 ${rem(40)};
  height: ${rem(40)};
  justify-content: center;
  width: ${rem(40)};
`

function renderStrongText(chunks: ReactNode): ReactNode {
  return <strong>{chunks}</strong>
}

interface Props {
  basePackage: App.Package
  upgradePackage: App.Package
  formattedUpgradePrice: string
  isUltraLux: boolean
  isDismissible: boolean
  checkIn?: string
  charterBundleRoute?: App.CharterBundleRoute
  hasFeederFlightIncluded: boolean
  propertyTimezoneOffset?: number
  loyaltyPointsContent?: ReactNode
  luxPlusInclusionsContent?: ReactNode
  onUpgradeClick: () => void
  onNoThanksClick: () => void
}

function StayUpgradeModalMobileVariant(props: Props) {
  const {
    basePackage,
    upgradePackage,
    formattedUpgradePrice,
    isUltraLux,
    isDismissible,
    checkIn,
    charterBundleRoute,
    hasFeederFlightIncluded,
    propertyTimezoneOffset,
    loyaltyPointsContent,
    luxPlusInclusionsContent,
    onUpgradeClick,
    onNoThanksClick,
  } = props
  const intl = useIntl()
  const { airport, fareClass, rooms } = useContext(OfferPageStateContext)
  const { inclusions: baseInclusions, bonusInclusions: baseBonusInclusions } = usePackageUpgradeInclusions(basePackage, {
    flightOrigin: airport,
    hasFeederFlightIncluded,
  })
  const { inclusions: upgradeInclusions, bonusInclusions: upgradeBonusInclusions } = usePackageUpgradeInclusions(upgradePackage, {
    flightOrigin: airport,
    hasFeederFlightIncluded,
  })
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: upgradePackage.duration,
    charterBundleRoute,
    rooms,
    fareClass,
    feederOriginAirportCode: airport?.code,
  })
  const baseStayUpgradeInclusions = useMemo(
    () => [...baseInclusions, ...baseBonusInclusions],
    [baseBonusInclusions, baseInclusions],
  )
  const upgradeStayUpgradeInclusions = useMemo(
    () => [...upgradeInclusions, ...upgradeBonusInclusions],
    [upgradeBonusInclusions, upgradeInclusions],
  )
  const stayUpgradeInclusions = useMemo(
    () => getStayUpgradeInclusions(baseStayUpgradeInclusions, upgradeStayUpgradeInclusions),
    [baseStayUpgradeInclusions, upgradeStayUpgradeInclusions],
  )
  const upgradeType = useMemo(
    () => getStayUpgradeType(basePackage, upgradePackage, stayUpgradeInclusions),
    [basePackage, stayUpgradeInclusions, upgradePackage],
  )
  const highlights = useMemo(() => {
    if (upgradeType === 'packageOnly') return []

    return getStayUpgradeHighlights(
      basePackage,
      upgradePackage,
      baseStayUpgradeInclusions,
      upgradeStayUpgradeInclusions,
    )
  }, [basePackage, baseStayUpgradeInclusions, upgradePackage, upgradeStayUpgradeInclusions, upgradeType])
  const upgradeHotelPackage = isHotelPackage(upgradePackage) ? upgradePackage : undefined
  const roomType = upgradeHotelPackage?.roomType
  const isPackageOnly = upgradeType === 'packageOnly'
  const hasRoomDetails = !isPackageOnly && !!roomType && (
    !!roomType.sizeSqm ||
    !!roomType.bedGroups.length ||
    !!roomType.numberOfBedrooms
  )
  const inclusionBadge = useMemo(() => {
    if (stayUpgradeInclusions.newCount === stayUpgradeInclusions.totalCount) {
      return intl.formatMessage(messages.allInclusionsNewBadge, { count: stayUpgradeInclusions.totalCount })
    }
    if (stayUpgradeInclusions.newCount > 0) {
      return intl.formatMessage(messages.someInclusionsNewBadge, { count: stayUpgradeInclusions.newCount })
    }
    return intl.formatMessage(messages.noNewInclusionsBadge, { count: stayUpgradeInclusions.totalCount })
  }, [intl, stayUpgradeInclusions.newCount, stayUpgradeInclusions.totalCount])

  const inclusionHeading = intl.formatMessage(messages.inclusionsHeading)

  const modalTitle = intl.formatMessage(isPackageOnly ? messages.packageOnlyTitle : messages.roomUpgradeTitle)

  let upgradeSubtitle: ReactNode
  if (isPackageOnly) {
    upgradeSubtitle = <BodyText variant="large">{upgradePackage.name}</BodyText>
  } else {
    upgradeSubtitle = <BodyText variant="large">
      <FormattedMessage
        id="j8n2yYoBunNIliV55mvI"
        defaultMessage="{baseRoomName} → <strong>{upgradeRoomName}</strong>"
        description="Selected-to-suggested room transition in the refreshed mobile room upgrade modal"
        values={{
          baseRoomName: basePackage.roomType?.name,
          upgradeRoomName: upgradePackage.roomType?.name,
          strong: renderStrongText,
        }}
      />
    </BodyText>
  }

  const upgradeButtonLabel = intl.formatMessage(messages.upgradeButton, { formattedUpgradePrice })
  const noThanksButtonLabel = intl.formatMessage(messages.noThanksButton)
  const primaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    onClick: onUpgradeClick,
    palette: isUltraLux ? 'ultralux' : undefined,
    children: upgradeButtonLabel,
  }), [isUltraLux, onUpgradeClick, upgradeButtonLabel])
  const secondaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    onClick: onNoThanksClick,
    children: noThanksButtonLabel,
  }), [noThanksButtonLabel, onNoThanksClick])

  return <ModalBase size="L" height="full" dismissible={isDismissible}>
    <ModalHeader title="" isOverlay dismissible={isDismissible} />
    <ModalBody>
      {!!roomType?.images.length && <ImageGallery images={roomType.images} imageParams={imageProps} />}
      <ModalContent>
        <VerticalSpacer gap={20}>
          <VerticalSpacer gap={8}>
            <Heading variant="heading2">{modalTitle}</Heading>
            {upgradeSubtitle}
            {upgradePackage.roomRate && <FlashCancellationPolicies
              checkInDate={checkIn}
              charterDepartDate={charterBundleRoute ? charterDepartDate : undefined}
              propertyTimezoneOffset={propertyTimezoneOffset}
              cancellationPolicy={upgradePackage.roomRate.cancellationPolicy}
            />}

            {loyaltyPointsContent}
          </VerticalSpacer>

          {!!highlights.length && <VerticalSpacer gap={12}>
            {highlights.map(highlight => {
              const Highlight = highlightIcons[highlight.id]
              return <Group key={highlight.id} direction="horizontal" gap={12} verticalAlign="center">
                <HighlightIcon><Highlight aria-hidden="true" /></HighlightIcon>
                <div>
                  <Heading variant="heading6">{intl.formatMessage(highlightMessages[`${highlight.id}-title`])}</Heading>
                  <BodyText variant="medium" colour="neutral-two">{intl.formatMessage(highlightMessages[`${highlight.id}-description`])}</BodyText>
                </div>
              </Group>
            })}
          </VerticalSpacer>}

          {hasRoomDetails && upgradeHotelPackage && <VerticalSpacer gap={8}>
            <Heading variant="heading4"><FormattedMessage
              id="MtIwTaNyWemwPyVQ7CTB"
              defaultMessage="Upgraded room details"
              description="Heading above room detail chips in the refreshed mobile room upgrade modal"
            />
            </Heading>
            <AccommodationRoomDetailsList
              roomPackage={upgradeHotelPackage}
              shouldShowAmenitiesAndPoliciesCta={false}
            />
          </VerticalSpacer>}

          {luxPlusInclusionsContent}

          {!!stayUpgradeInclusions.inclusions.length && <VerticalSpacer gap={8}>
            <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between">
              <Heading variant="heading4">{inclusionHeading}</Heading>
              <Pill palette="success">{inclusionBadge}</Pill>
            </Group>
            <InclusionsSection inclusions={stayUpgradeInclusions.inclusions} bonusInclusions={EMPTY_INCLUSIONS} size="medium" />
          </VerticalSpacer>}

          {roomType && <LePackageUpgradeCardRoomDetails roomType={roomType} />}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      actionsLayout="stacked"
      primaryActionProps={primaryActionProps}
      secondaryActionProps={secondaryActionProps}
    />
  </ModalBase>
}

export default StayUpgradeModalMobileVariant
